import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import SuccessToast from '../SuccessToast';
import ConfirmDialog from '../ConfirmDialog';
import { getApiUrl, apiRequest } from '../../config/api';

function RegistroQueriesPanel({
  selectedBotId,
  view,
  isAdmin,
  selectedRegistroQuery,
  registroQueries,
  registroQueryResult,
  registroQueryLoading,
  handleSelectRegistroQuery,
  executeRegistroQuery,
  onQueriesChanged,
}) {
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const [pendienteBorrar, setPendienteBorrar] = useState(null);
  const [adminRegistroQueriesBotId, setAdminRegistroQueriesBotId] = useState(null);
  const [showRegistroQueryModal, setShowRegistroQueryModal] = useState(false);
  const [editingRegistroQuery, setEditingRegistroQuery] = useState(null);
  const [registroQueryForm, setRegistroQueryForm] = useState({ name: '', description: '', sqlQuery: '', maxRows: 500, isActive: true });
  const [registroQuerySaving, setRegistroQuerySaving] = useState(false);

  const mostrarExito = (mensaje) => {
    setToastError(null);
    setToastExito(mensaje);
  };

  const mostrarError = (mensaje) => {
    setToastExito(null);
    setToastError(mensaje);
  };

  const openRegistroQueryModal = (botId, query = null) => {
    if (query) {
      setEditingRegistroQuery(query);
      setRegistroQueryForm({
        name: query.name,
        description: query.description || '',
        sqlQuery: query.sqlQuery,
        maxRows: query.maxRows || 500,
        isActive: query.isActive !== false
      });
    } else {
      setEditingRegistroQuery(null);
      setRegistroQueryForm({ name: '', description: '', sqlQuery: '', maxRows: 500, isActive: true });
    }
    setAdminRegistroQueriesBotId(botId);
    setShowRegistroQueryModal(true);
  };

  const handleSaveRegistroQuery = async (e) => {
    e.preventDefault();
    setRegistroQuerySaving(true);
    try {
      const url = editingRegistroQuery
        ? getApiUrl(`/bots/${adminRegistroQueriesBotId}/registro-queries/${editingRegistroQuery.id}`)
        : getApiUrl(`/bots/${adminRegistroQueriesBotId}/registro-queries`);
      const method = editingRegistroQuery ? 'PUT' : 'POST';

      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(registroQueryForm)
      });

      if (response.ok) {
        setShowRegistroQueryModal(false);
        mostrarExito(editingRegistroQuery ? 'Query actualizada correctamente' : 'Query creada correctamente');
        onQueriesChanged?.();
      } else {
        const err = await response.json();
        mostrarError(err.error || 'Error al guardar query');
      }
    } catch (error) {
      console.error('Error saving registro query:', error);
      mostrarError('Error de conexión');
    } finally {
      setRegistroQuerySaving(false);
    }
  };

  const handleDeleteRegistroQuery = async (botId, queryId) => {
    setPendienteBorrar(null);
    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/registro-queries/${queryId}`), {
        method: 'DELETE'
      });
      if (response.ok) {
        mostrarExito('Query eliminada');
        onQueriesChanged?.(queryId);
      } else {
        const err = await response.json().catch(() => ({}));
        mostrarError(err.error || 'Error al eliminar query');
      }
    } catch (error) {
      console.error('Error deleting registro query:', error);
      mostrarError('Error de conexión');
    }
  };

  if (view === 'sidebar') {
    return (
      <div className="registros-sidebar-list">
        {isAdmin && (
          <button type="button" className="admin-btn primary" onClick={() => openRegistroQueryModal(selectedBotId)}>
            Nueva consulta
          </button>
        )}
        <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
        <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />

        {registroQueries.length > 0 ? (
          registroQueries.map(q => (
            <div
              key={q.id}
              className={`registros-query-item ${selectedRegistroQuery?.id === q.id ? 'active' : ''} ${q.isActive === false ? 'inactiva' : ''}`}
              onClick={() => handleSelectRegistroQuery(q)}
            >
              <div className="registros-query-name">{q.name}</div>
              {q.description && <div className="registros-query-desc">{q.description}</div>}
              {q.isActive === false && <span className="registros-query-inactive">Inactiva</span>}
              {isAdmin && (
                <div className="registros-query-actions">
                  <button
                    type="button"
                    className="admin-btn-small"
                    onClick={(e) => { e.stopPropagation(); openRegistroQueryModal(selectedBotId, q); }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="admin-btn-small"
                    onClick={(e) => { e.stopPropagation(); setPendienteBorrar({ botId: selectedBotId, id: q.id, name: q.name }); }}
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="registros-empty-sidebar">
            <p>No hay consultas configuradas</p>
          </div>
        )}
        {pendienteBorrar && (
          <ConfirmDialog
            title="Eliminar consulta"
            note="Esta acción no se puede deshacer."
            onCancel={() => setPendienteBorrar(null)}
            onConfirm={() => handleDeleteRegistroQuery(pendienteBorrar.botId, pendienteBorrar.id)}
          >
            ¿Eliminar la consulta <strong>"{pendienteBorrar.name}"</strong>?
          </ConfirmDialog>
        )}
        {showRegistroQueryModal && createPortal(
          <div className="modal-overlay" onClick={() => setShowRegistroQueryModal(false)}>
            <div
              className="modal-content script-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="registro-query-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="registro-query-title">{editingRegistroQuery ? `Editar: ${editingRegistroQuery.name}` : 'Nueva consulta de registro'}</h3>
                <button className="modal-close" onClick={() => setShowRegistroQueryModal(false)}>×</button>
              </div>
              <form onSubmit={handleSaveRegistroQuery}>
                <div className="script-editor-body">
                  <div className="form-group">
                    <label htmlFor="rq-nombre">Nombre</label>
                    <input
                      id="rq-nombre"
                      type="text"
                      value={registroQueryForm.name}
                      onChange={e => setRegistroQueryForm({...registroQueryForm, name: e.target.value})}
                      placeholder="Ej: Leads recientes"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rq-descripcion">Descripción (opcional)</label>
                    <input
                      id="rq-descripcion"
                      type="text"
                      maxLength={500}
                      value={registroQueryForm.description}
                      onChange={e => setRegistroQueryForm({...registroQueryForm, description: e.target.value})}
                      placeholder="Breve descripción de la consulta"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rq-sql">Consulta SQL</label>
                    <textarea
                      id="rq-sql"
                      value={registroQueryForm.sqlQuery}
                      onChange={e => setRegistroQueryForm({...registroQueryForm, sqlQuery: e.target.value})}
                      placeholder="SELECT * FROM bot_script_data WHERE bot_id = 1 AND collection_name = 'leads' ORDER BY created_at DESC"
                      required
                      rows={6}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="admin-btn secondary" onClick={() => setShowRegistroQueryModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="admin-btn primary" disabled={registroQuerySaving}>
                    {registroQuerySaving ? 'Guardando...' : (editingRegistroQuery ? 'Guardar Cambios' : 'Crear Query')}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  if (view === 'main') {
    return (
      <div className="registros-main-content">
        {!selectedRegistroQuery ? (
          <div className="registros-placeholder">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="registros-icon">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h2>Registros</h2>
            <p>{registroQueries.length > 0 ? 'Selecciona una consulta del panel izquierdo' : 'No hay consultas configuradas para este bot'}</p>
          </div>
        ) : (
          <div className="registros-table-container">
            <div className="registros-table-header">
              <div className="registros-header-left">
                <button
                  className="registros-back-btn"
                  onClick={() => { window.history.back(); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                    <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div>
                  <h3>{selectedRegistroQuery.name}</h3>
                  {selectedRegistroQuery.description && <p className="registros-table-desc">{selectedRegistroQuery.description}</p>}
                </div>
              </div>
              <button
                className="admin-btn-small"
                onClick={() => executeRegistroQuery(selectedBotId, selectedRegistroQuery.id)}
                disabled={registroQueryLoading}
              >
                {registroQueryLoading ? 'Cargando...' : 'Actualizar'}
              </button>
            </div>

            {registroQueryLoading && !registroQueryResult && (
              <div className="registros-loading">Ejecutando consulta...</div>
            )}

            {registroQueryResult?.error && (
              <div className="registros-error">{registroQueryResult.error}</div>
            )}

            {registroQueryResult && !registroQueryResult.error && (
              <>
                <div className="registros-table-info">
                  <span>{registroQueryResult.returnedRows} de {registroQueryResult.totalRows} filas</span>
                  <span>{registroQueryResult.executionTimeMs}ms</span>
                  {registroQueryResult.truncated && <span className="registros-truncated">Resultados truncados</span>}
                </div>
                <div className="registros-table-wrapper">
                  <table className="registros-table">
                    <thead>
                      <tr>
                        {registroQueryResult.columns.map((col, i) => (
                          <th key={i}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {registroQueryResult.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell !== null ? cell : <span className="registros-null">NULL</span>}</td>
                          ))}
                        </tr>
                      ))}
                      {registroQueryResult.rows.length === 0 && (
                        <tr><td colSpan={registroQueryResult.columns.length} className="registros-no-data">Sin resultados</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default RegistroQueriesPanel;
